import React from "react";
import t1 from "../../assets/personal/training_lifestyle/t1.jpg";
import t2 from "../../assets/personal/training_lifestyle/t2.jpg";
import TrainingLifestyleIcon from "../../assets/my_journey_training_lifestyle_icon.png";

export default function TrainingLifestyle() {
  return (
    <section className="py-12 px-4 md:px-12 relative w-full overflow-hidden">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/30 z-0"></div>

      {/* Separator Line */}
      <div className="w-full flex justify-center mb-8">
        <div className="h-1 bg-gray-500 rounded-full animate-growLine w-4/5 max-w-[90%]"></div>
      </div>

      <div className="relative z-10 max-w-6xl w-full mx-auto flex flex-col gap-12">
        {/* Section Heading */}
        <div className="mb-8">
          {/* Heading */}
          <div className="flex items-center gap-4 w-full max-w-[90%] mb-8">
            <img
              src={TrainingLifestyleIcon}
              alt="Training lifestyle Icon"
              className="w-11 h-11 md:w-14 md:h-14"
            />
            <h1 className="text-white text-4xl md:text-5xl font-bold underline decoration-gray-400 decoration-1 underline-offset-2">
              Training Lifestyle
            </h1>
          </div>

          <p className="text-neonTurquoise italic text-lg md:text-xl mt-2 max-w-3xl text-left animate-fadeIn delay-200">
            A disciplined approach to training, recovery, and lifestyle choices
            that fuels performance and well-being.
          </p>
        </div>

        {/* First Block */}
        <div className="md:flex md:items-start md:gap-6 flex-col md:flex-row">
          <div className="md:w-1/2 flex flex-col gap-3">
            <p className="text-gray-200 text-lg md:text-xl leading-relaxed animate-fadeInLeft">
              My daily training routine is built on consistency and intention. I
              begin each day with a structured warm-up and mobility work,
              followed by focused strength and endurance sessions. Emphasizing
              correct form and gradual progression ensures sustainable results
              while preventing injuries. Recovery practices, including
              stretching, foam rolling, and mindful rest, are integrated to
              maintain energy levels and reduce fatigue.
            </p>

            <p className="text-gray-200 text-lg md:text-xl leading-relaxed animate-fadeInLeft delay-200">
              Nutrition and lifestyle choices complement training, fueling both
              performance and recovery. I prioritize balanced meals, hydration,
              and sufficient sleep while managing stress and daily
              responsibilities.
            </p>

            <ul className="text-gray-200 text-lg md:text-xl list-disc list-inside mt-3">
              {[
                "Structured warm-up and mobility drills at the start of each session.",
                "Balanced rotation of strength, endurance, and functional movements.",
                "Focus on correct form to prevent strain and maintain stability.",
                "Daily recovery work through stretching and foam-rolling routines.",
                "Hydration tracking to support energy levels during training.",
                "Consistent sleep schedule to aid muscle repair and performance.",
                "Routine adjustments based on weekly training response and fatigue.",
              ].map((item, index) => (
                <li
                  key={index}
                  className="transform transition-transform duration-300 hover:text-gray-400 animate-fadeInLeft"
                  style={{
                    animationDelay: `${index * 200}ms`,
                    animationFillMode: "both",
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:w-1/2 flex justify-center relative animate-fadeInRight mt-4 md:mt-0">
            <img
              src={t1}
              alt="Training Routine"
              className="w-full h-auto object-contain rounded-xl shadow-2xl transform transition-transform duration-500 hover:scale-105"
            />
          </div>
        </div>

        {/* Second Block */}
        <div className="md:flex md:items-start md:gap-6 flex-col md:flex-row-reverse">
          <div className="md:w-1/2 flex flex-col gap-3">
            <p className="text-gray-200 text-lg md:text-xl leading-relaxed animate-fadeInRight">
              Beyond the physical aspect, my training lifestyle relies on a
              clear mindset, steady focus, and planned execution. I document
              each session through detailed logs, noting workload, pacing, and
              response. This record helps me evaluate progress with accuracy and
              adjust routines based on performance. I rely on consistent
              reflection to decide when to increase intensity or when to pull
              back to maintain balance. This structure keeps training
              purposeful, steady, and aligned with long-term goals.
            </p>

            <p className="text-gray-200 text-lg md:text-xl leading-relaxed animate-fadeInRight delay-200">
              This approach supports constant progress without burnout. Recovery
              is placed at the same level of importance as training itself.
              Adjustments are made through weekly evaluations, allowing the body
              to stay prepared for each phase. This method maintains discipline
              while giving space for physical and mental reset, creating a
              stable foundation for continuous improvement.
            </p>

            <ul className="text-gray-200 text-lg md:text-xl mb-6 list-disc list-inside">
              {[
                "Daily review of training notes for accurate adjustments.",
                "Clear weekly targets to track improvement.",
                "Strategic rest periods based on training demand.",
                "Simple performance checks to guide progression.",
                "Balanced routines that match physical and mental readiness.",
              ].map((item, index) => (
                <li
                  key={index}
                  className="transform transition-transform duration-300 hover:text-gray-400 animate-fadeInRight"
                  style={{
                    animationDelay: `${index * 200}ms`,
                    animationFillMode: "both",
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:w-1/2 flex justify-center relative animate-fadeInLeft mt-4 md:mt-0">
            <img
              src={t2}
              alt="Training and Lifestyle"
              className="w-full h-auto object-contain rounded-xl shadow-2xl transform transition-transform duration-500 hover:scale-105"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
